import { useRef } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Download, ExternalLink, Link2 } from "lucide-react";
import { Button } from "./ui/button";

function LinkRow({ id, label, hint, url }) {
  const qr = useRef(null);
  const download = () => {
    const svg = qr.current?.querySelector("svg");
    if (!svg) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([svg.outerHTML], { type: "image/svg+xml" }));
    a.download = `qr-${id}.svg`;
    a.click();
  };
  return (
    <div className="flex gap-4 items-center rounded-[var(--radius)] border bg-card p-4" data-testid={`clinic-link-${id}`}>
      <div ref={qr} className="shrink-0 rounded-md bg-white p-1.5 border"><QRCodeSVG value={url} size={72} /></div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="font-semibold text-sm">{label}</div>
        <p className="text-xs text-muted-foreground">{hint}</p>
        <p className="text-xs font-mono truncate" data-testid={`clinic-link-${id}-url`}>{url}</p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Button size="sm" variant="outline" data-testid={`clinic-link-${id}-copy`} onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
          <Button size="sm" variant="outline" asChild><a href={url} target="_blank" rel="noreferrer" data-testid={`clinic-link-${id}-open`}><ExternalLink className="h-3.5 w-3.5 mr-1" />Open</a></Button>
          <Button size="sm" variant="ghost" data-testid={`clinic-link-${id}-qr`} onClick={download}><Download className="h-3.5 w-3.5 mr-1" />QR code</Button>
        </div>
      </div>
    </div>
  );
}

export function ClinicLinks({ origin, slug }) {
  const links = [
    ["home-page", "Clinic home page", "Your one link to share everywhere: book, sign up, log in, gift cards and shop.", `/c/${slug}`],
    ["staff-login", "Staff login", "For your team only: your logo and colours, no Bloomwell. Save it as a bookmark.", `/login/${slug}`],
    ["book", "Book an appointment", "Opens straight on your booking page.", `/book/${slug}`],
    ["signup", "Patient sign-up", "New patients create an account (you approve them).", `/p/${slug}?mode=register`],
    ["login", "Patient login", "Your patient app: appointments, packages, messages.", `/p/${slug}`],
    ["home-visits", "Home visits", "Book a practitioner at home.", `/home/${slug}`],
    ["packages", "Packages & gift cards", "Courses, bundles and gift cards to buy online.", `/packages/${slug}`],
    ["shop", "Shop", "Your product shop.", `/shop/${slug}`],
  ];
  return (
    <div className="lg:col-span-2 space-y-3" data-testid="clinic-links-panel">
      <div>
        <div className="flex items-center gap-2 font-semibold"><Link2 className="h-4 w-4 text-primary" />Your clinic links</div>
        <p className="text-sm text-muted-foreground mt-1">These open directly on your clinic with your name, logo and colours. Patients never need to search for you. Use them on your website, Instagram, WhatsApp, emails or printed QR codes.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">{links.map(([id, label, hint, path]) => <LinkRow key={id} id={id} label={label} hint={hint} url={origin + path} />)}</div>
    </div>
  );
}
